import { useCallback } from 'react';

import { backerKeys, useConsumerUnsubscribeToCause } from '@givebacks/api/backer';
import { useQueryClient } from '@tanstack/react-query';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, FullWidthButtonGroup, Modal, Stack, Text, notifications } from 'ui';
import { useDisclosure } from 'ui/Hooks';

const UnsubscribeModalContent = ({
  onClose,
  onCancel,
  causeId,
  token,
  onUnsubscribe,
}: {
  onClose?: () => void;
  onCancel?: () => void;
  causeId?: string;
  token?: string;
  onUnsubscribe: () => void;
}) => {
  const queryClient = useQueryClient();
  const { mutate: unsubscribeToCause, isPending: isLoading } = useConsumerUnsubscribeToCause();

  const onUnsubscribeFromAll = useCallback(() => {
    unsubscribeToCause(
      { causeId: causeId, token: token },
      {
        onSuccess: () => {
          notifications.show({
            message: `Unsubscribed from cause`,
            variant: 'success',
          });
          const key = backerKeys.consumer.backer.subscriptions.all;
          void queryClient.invalidateQueries({ queryKey: key });
          onUnsubscribe();
        },
        onError: (e) => {
          notifications.show({ message: getErrorMessage_v2(e), variant: 'error' });
        },
      }
    );
  }, [unsubscribeToCause, causeId, token, queryClient, onUnsubscribe]);

  return (
    <Stack gap="lg">
      <Stack>
        <Text fw={600} fz={16} c="gray.8">
          Are you sure you want to unsubscribe from all communication?
        </Text>
        <Text fw={400} fz={14} c="gray.6">
          If you unsubscribe from all messages, you will not receive any updates or communications
          from this cause. This includes messages related to events, campaigns, or purchases.
          However, you will still receive necessary transactional emails, such as receipts.
        </Text>
      </Stack>
      <FullWidthButtonGroup wrap="wrap-reverse">
        <ButtonMantine onClick={onCancel ?? onClose} variant="subtle" miw="50%">
          Cancel
        </ButtonMantine>
        <ButtonMantine onClick={onUnsubscribeFromAll} color="cerise" loading={isLoading}>
          Unsubscribe from All
        </ButtonMantine>
      </FullWidthButtonGroup>
    </Stack>
  );
};

const UnsubscribeModal = ({
  opened,
  onClose,
  causeId,
  token,
}: {
  opened: boolean;
  onClose: () => void;
  causeId?: string;
  token?: string;
}) => {
  return (
    <Modal title="Unsubscribe" opened={opened} onClose={onClose}>
      <UnsubscribeModalContent
        onClose={onClose}
        causeId={causeId}
        token={token}
        onUnsubscribe={onClose}
      />
    </Modal>
  );
};

const UnsubscribeFromCauseButton = ({
  causeId,
  token,
  fullWidth,
  onUnsubscribe,
}: {
  causeId?: string;
  token?: string;
  fullWidth?: boolean;
  onUnsubscribe?: () => void;
}) => {
  const [opened, handler] = useDisclosure();
  return (
    <>
      <ButtonMantine
        color="cerise"
        size={fullWidth ? 'md' : 'sm'}
        fullWidth={fullWidth}
        onClick={() => {
          if (onUnsubscribe) {
            onUnsubscribe();
          } else {
            handler.open();
          }
        }}
      >
        Unsubscribe from All
      </ButtonMantine>
      <UnsubscribeModal opened={opened} onClose={handler.close} causeId={causeId} token={token} />
    </>
  );
};

export { UnsubscribeFromCauseButton, UnsubscribeModalContent };
